import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ENV } from '../config/env';
import BusinessPlanDocument from '../components/business-plan/BusinessPlanDocument';
import BusinessPlanSharedComments from '../components/business-plan/BusinessPlanSharedComments';
import type { BusinessPlan } from '../types/businessPlan';

interface SharedPlanResponse {
  share: {
    id: string;
    business_plan_id: string;
    access_type: 'view' | 'edit';
    share_token: string;
    expires_at: string | null;
  };
  plan: any;
}

const normalizePlan = (row: any): BusinessPlan => {
  // Backend returns business_plans row. Some fields differ between historical schemas.
  // Normalize to the frontend BusinessPlan shape.
  return {
    id: String(row.id),
    idea_id: String(row.idea_id || ''),
    user_id: String(row.user_id || ''),
    version: typeof row.version === 'number' ? row.version : Number(row.version || 1),
    sections: (row.sections || {}) as any,
    content: row.content || {},
    created_at: row.created_at || new Date().toISOString(),
    updated_at: row.updated_at || new Date().toISOString(),
    status: row.status || 'completed',
    title: row.title || 'Business Plan',
  } as any;
};

const BusinessPlanShared: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [plan, setPlan] = useState<BusinessPlan | null>(null);
  const [shareAccess, setShareAccess] = useState<'view' | 'edit'>('view');

  const expiresText = useMemo(() => {
    if (!plan) return null;
    return null;
  }, [plan]);

  useEffect(() => {
    const run = async () => {
      if (!token) {
        setError('Missing share token');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const resp = await fetch(`${ENV.API_URL}/api/business-plans/public/shares/${token}`);
        if (!resp.ok) {
          const txt = await resp.text();
          throw new Error(txt || 'Share link not found');
        }

        const json = (await resp.json()) as SharedPlanResponse;
        setShareAccess(json.share?.access_type || 'view');
        setPlan(normalizePlan(json.plan));
      } catch (e: any) {
        console.error('Failed to load shared plan:', e);
        const msg = e?.message || 'Failed to load shared plan';
        setError(msg);
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [token]);

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="mx-auto max-w-6xl px-4 py-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-gray-900">Shared business plan</div>
            <div className="mt-1 text-xs text-gray-600">Access: {shareAccess}</div>
            {expiresText ? <div className="mt-1 text-xs text-gray-600">{expiresText}</div> : null}
          </div>
          <Link
            to="/business-plan"
            className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-gray-200 transition hover:bg-gray-50"
          >
            Open Explanatum
          </Link>
        </div>

        {plan ? (
          <div className="space-y-4">
            <BusinessPlanDocument
              plan={plan}
              loading={loading}
              error={error}
              refreshPlan={() => {
                window.location.reload();
              }}
              readOnly={true}
            />
            {token ? <BusinessPlanSharedComments token={token} /> : null}
          </div>
        ) : (
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            {loading ? <div className="text-sm text-gray-600">Loading shared plan…</div> : null}
            {!loading && error ? <div className="text-sm text-red-700">{error}</div> : null}
            {!loading && !error ? <div className="text-sm text-gray-600">No plan loaded.</div> : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessPlanShared;
